import { useTenant } from "@/hooks/useTenant";
import { portalFetch } from "@/lib/portalApi";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

/**
 * SIM-1417 — typed dashboard property definitions per tenant. Mirror of the
 * server-side schema in apps/platform/src/orpc/routes/portal/properties.
 */
export interface PropertyDef {
  id: string;
  tenantId: string;
  key: string;
  label: string;
  type: string;
  options: string[];
  order: number;
  createdAt: string;
  updatedAt: string;
}

export function useProperties() {
  const { currentTenantId } = useTenant();

  const { data, isLoading, error } = useQuery({
    queryKey: ["portal-properties", currentTenantId],
    queryFn: () => portalFetch<PropertyDef[]>(`/api/portal/tenants/${currentTenantId}/properties`),
    enabled: !!currentTenantId,
  });

  return { data: data ?? [], isLoading, error };
}

export function useCreateProperty() {
  const { currentTenantId } = useTenant();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (input: { key: string; label: string; type: string; options: string[] }) =>
      portalFetch<PropertyDef>(`/api/portal/tenants/${currentTenantId}/properties`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId: currentTenantId, ...input }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portal-properties"] }),
  });
}

export function useUpdateProperty() {
  const { currentTenantId } = useTenant();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (input: { id: string; label?: string; options?: string[]; order?: number }) =>
      portalFetch<PropertyDef>(`/api/portal/tenants/${currentTenantId}/properties/${input.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId: currentTenantId, ...input }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portal-properties"] }),
  });
}

export function useDeleteProperty() {
  const { currentTenantId } = useTenant();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      portalFetch(`/api/portal/tenants/${currentTenantId}/properties/${id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId: currentTenantId, id }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["portal-properties"] }),
  });
}
